import { useEffect, useRef, useState, type FormEvent } from "react";
import { AccessError, unlockAccess } from "./lib/api";
import "./access-gate.css";

const validChannel = (value: string) => /^\d{1,2}$/.test(value) && Number(value) >= 2 && Number(value) <= 69;
const formatChannel = (value: string) => validChannel(value) ? String(Number(value)).padStart(2, "0") : value;

export function AccessGate({ checking, onUnlocked }: { checking: boolean; onUnlocked: () => void }) {
  const [values, setValues] = useState(["02", "02", "02"]);
  const [invalid, setInvalid] = useState<number[]>([]);
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const inFlight = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!cooldownUntil) return;
    const update = () => {
      const seconds = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setRemaining(seconds);
      if (!seconds) setCooldownUntil(0);
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownUntil]);

  function change(index: number, value: string) {
    setValues(previous => previous.map((current, i) => i === index ? value : current));
    setInvalid(previous => previous.filter(i => i !== index));
    setMessage("");
  }

  function step(index: number, amount: number) {
    const current = validChannel(values[index]!) ? Number(values[index]) : 2;
    change(index, String(Math.max(2, Math.min(69, current + amount))).padStart(2, "0"));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (checking || inFlight.current || Date.now() < cooldownUntil) return;
    const errors = values.flatMap((value, index) => validChannel(value) ? [] : [index]);
    setInvalid(errors);
    if (errors.length) {
      setMessage("Enter a whole number from 2 to 69 for each channel.");
      inputs.current[errors[0]!]?.focus();
      return;
    }
    inFlight.current = true;
    setPending(true);
    setMessage("");
    setValues(previous => previous.map(formatChannel));
    try {
      const result = await unlockAccess(values.map(Number) as [number, number, number]);
      if (!mounted.current) return;
      if (result.unlocked) { onUnlocked(); return; }
      setMessage("Those channel numbers don’t match. Please try again.");
    } catch (error) {
      if (!mounted.current) return;
      if (error instanceof AccessError && error.status === 429) {
        const seconds = error.retryAfterSeconds ?? 60;
        setRemaining(seconds);
        setCooldownUntil(Date.now() + seconds * 1000);
        setMessage("");
      } else {
        setMessage(error instanceof AccessError && error.status === 401
          ? "Those channel numbers don’t match. Please try again."
          : "Couldn’t connect. Your numbers are saved here—please try again.");
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setPending(false);
    }
  }

  const status = checking ? "Checking access…" : pending ? "Unlocking…" : remaining > 0
    ? `Too many attempts. Try again in ${remaining} seconds.` : message;

  return (
    <main className="receiver-access" aria-label="SerendipTV access lock">
      <section className="receiver-cabinet">
        <form className="receiver-panel" onSubmit={submit} noValidate aria-labelledby="access-title" aria-busy={checking || pending}>
          <div className="receiver-screws" aria-hidden="true"><i /><i /><i /><i /></div>
          <div className="receiver-brand">SERENDIPTV</div>
          <div className="receiver-rule" aria-hidden="true" />
          <h1 id="access-title">Unlock your television</h1>
          <p className="receiver-instruction">Enter your three channel numbers.</p>
          <div className="receiver-channels">
            {values.map((value, index) => (
              <div className="receiver-channel" key={index}>
                <label htmlFor={`access-channel-${index}`}>CHANNEL {index + 1}</label>
                <input id={`access-channel-${index}`} ref={element => { inputs.current[index] = element; }}
                  type="text" inputMode="numeric" autoComplete="off" spellCheck={false}
                  value={value} disabled={checking || pending} aria-invalid={invalid.includes(index)}
                  aria-describedby={invalid.includes(index) ? "access-message" : undefined}
                  onChange={event => change(index, event.currentTarget.value)}
                  onBlur={() => setValues(previous => previous.map((current, i) => i === index ? formatChannel(current) : current))}
                  onFocus={event => event.currentTarget.select()} />
                <div className="receiver-adjustments">
                  <button type="button" aria-label={`Lower channel ${index + 1}`} disabled={checking || pending || Number(value) === 2} onClick={() => step(index, -1)}>−</button>
                  <button type="button" aria-label={`Raise channel ${index + 1}`} disabled={checking || pending || Number(value) === 69} onClick={() => step(index, 1)}>+</button>
                </div>
              </div>
            ))}
          </div>
          <button className="receiver-unlock" type="submit" disabled={checking || pending || remaining > 0}>UNLOCK</button>
          <p id="access-message" className="receiver-message" role="status" aria-live="polite">{status}</p>
        </form>
      </section>
    </main>
  );
}
